<template>
    <div class="camera-section">
        <div class="camera-container">
            <span class='title'><span class="title-gradient">实时监控画面</span></span>
            <span class="angle1"></span>
            <span class="angle2"></span>
            <span class="angle3"></span>
            <span class="angle4"></span>
            <div class="video-wrapper">
                <!-- 使用v-once防止播放器容器被重新渲染 -->
                <div id="camera-player" ref="cameraPlayer"></div>
                <div v-if="videoLoading" class="video-loading">
                  <div class="loading-spinner"></div>
                  <p>视频加载中...</p>
                </div>
                <!-- 摄像头列表 - 右上角 -->
                <div v-if="cameraList.length > 1" class="camera-list-top-right">
                    <div class="camera-list-items">
                        <div 
                            v-for="(camera, index) in cameraList" 
                            :key="camera.id"
                            :class="['camera-item', { active: index === currentCameraIndex }]"
                            @click="switchCamera(index)"
                            :title="camera.name || '摄像头' + (index + 1)"
                        >
                            {{ camera.name || '摄像头' + (index + 1) }}
                        </div>
                    </div>
                </div>
            </div>
            <!-- 使用播放器自带控制栏（包含播放、暂停、静音、全屏等功能） -->
            <!-- 采集按钮区域 -->
            <div class="camera-capture">
                <button 
                    @click="selectSavePath" 
                    class="path-btn"
                    :disabled="isCapturing"
                >
                    选择保存路径
                </button>
                <span v-if="savePath" class="save-path">
                    保存路径: {{ savePath }}
                </span>
                <button 
                    @click="toggleCapture" 
                    :class="['capture-btn', { 'capturing': isCapturing }]"
                >
                    {{ isCapturing ? '停止采集' : '开始采集' }}
                </button>
                <span v-if="isCapturing" class="capture-status">
                    正在采集中... (已采集 {{ captureCount }} 张)
                </span>
                <!-- 调试信息（开发时可见） -->
                <span v-if="!player && !videoError" class="debug-info" style="font-size: 11px; color: #888;">
                    等待播放器初始化...
                </span>
        </div>
        </div>
    </div>
</template>

<script>
/* eslint-disable no-undef */
import request from '@/utils/request'

const api = request

export default {
    name: 'center',
    props: {
        selectRangeDate: Array,
        pondsid: Number,
        companyId: Number,
        greenhouseId: Number
    },
    data() {
        return {
            // 萤石云相关数据
            accessToken: '',
            player: null,
            playerInitialized: false,
            // 加载状态
            videoLoading: false,
            videoError: false,
            isPlaying: false,
            isMuted: false,
            // 摄像头设备信息
            deviceSerial: '',
            channelNo: 1,
            cameraTimer: null,
            // 摄像头列表相关
            cameraList: [], // 摄像头列表
            currentCameraIndex: 0, // 当前播放的摄像头索引
            carouselTimer: null, // 轮播定时器
            carouselInterval: 30000, // 轮播间隔（30秒）
            // 采集相关数据
            isCapturing: false, // 是否正在采集
            captureTimer: null, // 采集定时器
            captureCount: 0, // 采集计数
            captureInterval: 5000, // 抓拍时间间隔（毫秒），从后端获取
            savePath: '', // 保存路径（浏览器限制，实际使用下载方式）
            saveDirectoryHandle: null, // File System Access API的目录句柄
            // ========== 新增：优化请求控制 ==========
            isLoadingCamera: false, // 正在加载摄像头
            lastLoadedParams: null, // 上次加载的参数缓存
            cameraUrlCache: new Map(), // 直播地址缓存
            debounceTimer: null, // 防抖定时器
            currentVideoUrl: '', // 当前播放的URL
        };
    },
    methods: {
        // 获取抓拍时间间隔配置
        async loadCaptureInterval() {
            try {
                console.log('开始获取抓拍间隔配置...');
                const response = await api.get('/api/ezviz/capture-interval');
                console.log('API响应:', response);
                // 注意：request.js拦截器已经自动解包，response就是服务端返回的{code, data}对象
                if (response && response.code === 200 && response.data !== null && response.data !== undefined) {
                    this.captureInterval = response.data;
                    console.log('获取抓拍间隔配置成功:', this.captureInterval, 'ms');
                } else {
                    console.warn('API返回数据格式不正确，使用默认值:', response);
                }
            } catch (error) {
                console.error('获取抓拍间隔配置失败，使用默认值:', error);
            }
        },

        // ========== 新增：防抖加载摄像头列表 ==========
        debounceLoadCamera() {
            // 清除之前的防抖定时器
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
            }
            // 设置新的防抖定时器，300ms后执行
            this.debounceTimer = setTimeout(() => {
                if (this.companyId && this.greenhouseId) {
                    this.loadCameraList();
                }
            }, 300);
        },

        // 加载摄像头列表
        async loadCameraList() {
            try {
                // 设置加载状态
                this.isLoadingCamera = true;
                console.log('========== 开始加载摄像头列表 ==========');
                console.log('公司ID:', this.companyId);
                console.log('大棚ID:', this.greenhouseId);

                if (!this.companyId || !this.greenhouseId) {
                    console.warn('公司ID或大棚ID为空，无法加载摄像头列表');
                    console.log('companyId:', this.companyId, 'greenhouseId:', this.greenhouseId);
                    this.cameraList = [];
                    this.isLoadingCamera = false;
                    return;
                }

                // 检查参数是否变化，如果没有变化则跳过
                const currentParams = `${this.companyId}-${this.greenhouseId}`;
                if (this.lastLoadedParams === currentParams && this.cameraList.length > 0) {
                    console.log('参数未变化，跳过重复请求');
                    this.isLoadingCamera = false;
                    return;
                }

                // 强制清空旧数据，确保切换时显示正确的列表
                this.cameraList = [];
                this.currentCameraIndex = 0;
                this.stopCarousel(); // 停止轮播
                this.destroyPlayer(); // 销毁播放器
                this.cameraUrlCache.clear(); // 清空URL缓存

                // 查询逻辑：根据 company_id 和 greenhouse_id 同时匹配查询
                // SQL: SELECT * FROM ezviz_device WHERE company_id = ? AND greenhouse_id = ?
                const response = await api.get('/api/ezviz/devices', {
                    params: {
                        companyId: this.companyId,
                        greenhouseId: this.greenhouseId
                    }
                });

                console.log('后端返回数据:', response);

                // 注意：request.js拦截器已经自动解包，response就是服务端返回的{code, data}对象
                if (response && response.code === '200') {
                    this.cameraList = response.data || [];
                    // 更新缓存的参数
                    this.lastLoadedParams = currentParams;
                    console.log('摄像头列表加载成功，共', this.cameraList.length, '个摄像头');
                    console.log('摄像头详情:', this.cameraList);
                    
                    // 数据标准化：确保字段名统一（处理可能的字段名差异）
                    this.cameraList = this.cameraList.map(camera => {
                        // 统一字段名：deviceSerial 或 device_serial 都转为 deviceSerial
                        if (camera.device_serial && !camera.deviceSerial) {
                            camera.deviceSerial = camera.device_serial;
                        }
                        // 统一字段名：channel 确保是数字
                        if (camera.channel != null) {
                            camera.channel = typeof camera.channel === 'string' 
                                ? parseInt(camera.channel, 10) || 4 
                                : camera.channel;
                        } else {
                            camera.channel = 4; // 默认值
                        }
                        console.log('标准化后的摄像头数据:', camera);
                        return camera;
                    });
                    
                    // 如果有摄像头，重置当前索引并开始播放
                    if (this.cameraList.length > 0) {
                        this.currentCameraIndex = 0;
                        this.loadCameraStream();
                        // 如果多个摄像头，启动轮播
                        if (this.cameraList.length > 1) {
                            this.startCarousel();
                        } else {
                            this.stopCarousel();
                        }
                    } else {
                        console.warn('该大棚下没有摄像头设备');
                        this.cameraList = [];
                        this.stopCarousel();
                    }
                } else {
                    throw new Error('获取摄像头列表失败: ' + (response.data.message || '未知错误'));
                }
            } catch (error) {
                console.error('加载摄像头列表失败:', error);
                this.cameraList = [];
                this.stopCarousel();
            } finally {
                // 确保加载状态被重置
                this.isLoadingCamera = false;
            }
        },

        //摄像头相关方法
        async loadCameraStream() {
            // 如果摄像头列表为空，先加载列表
            if (this.cameraList.length === 0) {
                await this.loadCameraList();
                return;
            }

            // 检查当前索引是否有效
            if (this.currentCameraIndex < 0 || this.currentCameraIndex >= this.cameraList.length) {
                this.currentCameraIndex = 0;
            }

            const currentCamera = this.cameraList[this.currentCameraIndex];
            if (!currentCamera) {
                console.error('当前摄像头不存在');
                return;
            }

            try {
                this.videoLoading = true;
                this.videoError = false;

                console.log(`开始加载摄像头视频流... (${this.currentCameraIndex + 1}/${this.cameraList.length})`, currentCamera);

                // 先加载EZUIKit SDK
                await this.loadEzuikit();

                // 获取AccessToken
                await this.getAccessToken();

                // 使用当前选中的摄像头设备
                let deviceSerial = currentCamera.deviceSerial;
                let channelNo = currentCamera.channel;

                // 数据验证和日志
                console.log('========== 摄像头数据验证 ==========');
                console.log('原始数据:', currentCamera);
                console.log('deviceSerial (原始):', deviceSerial, '类型:', typeof deviceSerial);
                console.log('channel (原始):', channelNo, '类型:', typeof channelNo);

                // 验证deviceSerial
                if (!deviceSerial || typeof deviceSerial !== 'string' || deviceSerial.trim() === '') {
                    console.error('deviceSerial无效:', deviceSerial);
                    throw new Error('摄像头设备序列号无效: ' + deviceSerial);
                }
                deviceSerial = deviceSerial.trim();

                // 验证channelNo
                if (channelNo == null || channelNo === undefined) {
                    console.warn('channel为空，使用默认值4');
                    channelNo = 4;
                } else if (typeof channelNo === 'string') {
                    // 如果是字符串，尝试转换为数字
                    const parsed = parseInt(channelNo, 10);
                    if (isNaN(parsed)) {
                        console.warn('channel不是有效数字，使用默认值4');
                        channelNo = 4;
                    } else {
                        channelNo = parsed;
                    }
                } else if (typeof channelNo !== 'number') {
                    console.warn('channel类型不正确，使用默认值4');
                    channelNo = 4;
                }

                console.log('验证后的数据:');
                console.log('deviceSerial:', deviceSerial);
                console.log('channelNo:', channelNo, '类型:', typeof channelNo);
                console.log('=====================================');

                // ========== 新增：使用缓存的直播地址，避免重复请求 ==========
                const cacheKey = `${deviceSerial}_${channelNo}`;
                let videoUrl = this.cameraUrlCache.get(cacheKey);

                if (videoUrl) {
                    console.log('========== 使用缓存的直播地址 ==========');
                    console.log('缓存Key:', cacheKey);
                    console.log('缓存URL:', videoUrl);
                    console.log('=========================================');
                    
                    // 复用缓存的地址，直接初始化播放器
                    this.cameraUrl = videoUrl;
                    this.initOrUpdatePlayer(videoUrl);
                    return;
                }
                console.log('========== 缓存未命中，需要请求新地址 ==========');

                // 调用后端API获取摄像头直播地址（ezopen协议）
                const response = await api.get('/api/ezviz/live-url', {
                    params: {
                        deviceSerial: deviceSerial,
                        channelNo: channelNo,
                        companyId: this.companyId,
                        greenhouseId: this.greenhouseId
                    }
                });

                if (response && response.code === '200') {
                    const videoUrl = response.data;
                    console.log('获取到摄像头地址:', videoUrl);

                    if (!videoUrl) {
                        throw new Error('获取到的视频地址为空');
                    }

                    // ========== 新增：保存到缓存 ==========
                    this.cameraUrlCache.set(cacheKey, videoUrl);
                    console.log('直播地址已缓存，Key:', cacheKey, 'URL:', videoUrl);

                    // 设置视频源
                    this.cameraUrl = videoUrl;

                    // 如果播放器已存在且已初始化，使用setUrl切换视频源（更高效）
                    if (this.player && this.playerInitialized && !this.videoError) {
                        console.log('播放器已存在，使用setUrl切换视频源');
                        try {
                            this.player.stop(); // 先停止当前播放
                            this.player.setUrl(videoUrl); // 设置新URL
                            this.player.play(); // 开始播放
                            this.videoLoading = false;
                            console.log('视频源切换成功');
                        } catch (e) {
                            console.warn('使用setUrl切换失败，重新初始化播放器:', e);
                            // 如果setUrl失败，则销毁重建
                            this.destroyPlayer();
                            this.$nextTick(() => {
                                this.initEzuikitPlayer(videoUrl);
                            });
                        }
                    } else {
                        // 播放器不存在或未初始化，创建新播放器
                        this.destroyPlayer();
                        this.$nextTick(() => {
                            this.initEzuikitPlayer(videoUrl);
                        });
                    }

                } else {
                    throw new Error('获取摄像头地址失败: ' + (response.data.message || '未知错误'));
                }

            } catch (error) {
                console.error('加载摄像头失败:', error);
                this.videoLoading = false;
                this.videoError = true;
            }
        },

        // 切换到指定摄像头
        switchCamera(index) {
            if (index < 0 || index >= this.cameraList.length) {
                console.warn('摄像头索引无效:', index);
                return;
            }

            // 停止轮播，避免冲突
            this.stopCarousel();

            // 清除之前的防抖定时器
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = null;
            }

            // 完全销毁之前的播放器
            this.destroyPlayer();

            // 设置新的防抖延迟，给之前的流时间释放
            this.debounceTimer = setTimeout(() => {
                this.currentCameraIndex = index;
                this.loadCameraStream();

                // 加载完成后再启动轮播
                if (this.cameraList.length > 1) {
                    this.startCarousel();
                }
            }, 800);
        },

        // 切换到下一个摄像头
        nextCamera() {
            if (this.cameraList.length === 0) return;
            const nextIndex = (this.currentCameraIndex + 1) % this.cameraList.length;
            this.switchCamera(nextIndex);
        },

        // 切换到上一个摄像头
        prevCamera() {
            if (this.cameraList.length === 0) return;
            const prevIndex = (this.currentCameraIndex - 1 + this.cameraList.length) % this.cameraList.length;
            this.switchCamera(prevIndex);
        },

        // 启动轮播
        startCarousel() {
            this.stopCarousel(); // 先停止已有的轮播
            if (this.cameraList.length > 1) {
                this.carouselTimer = setInterval(() => {
                    this.nextCamera();
                }, this.carouselInterval);
                console.log('摄像头轮播已启动，间隔:', this.carouselInterval / 1000, '秒');
            }
        },

        // 停止轮播
        stopCarousel() {
            if (this.carouselTimer) {
                clearInterval(this.carouselTimer);
                this.carouselTimer = null;
                console.log('摄像头轮播已停止');
            }
        },

        // 加载EZUIKit SDK
        loadEzuikit() {
            return new Promise((resolve, reject) => {
                if (window.EZUIKit) {
                    resolve();
                    return;
                }

                const script = document.createElement('script');
                script.src = '/js/ezuikit.js';
                script.onload = () => {
                    console.log('EZUIKit SDK加载成功');
                    resolve();
                };
                script.onerror = () => {
                    console.error('EZUIKit SDK加载失败');
                    reject(new Error('加载SDK失败'));
                };
                document.head.appendChild(script);
            });
        },

        // 获取AccessToken
        async getAccessToken() {
            try {
                const response = await api.get('/api/ezviz/access-token');
                // 注意：request.js拦截器已经自动解包，response就是服务端返回的{code, data}对象
                if (response && response.code === '200' && response.data) {
                    this.accessToken = response.data;
                    console.log('获取到AccessToken');
                } else {
                    throw new Error('获取Token失败');
                }
            } catch (error) {
                console.error('获取accessToken出错:', error);
                throw new Error('无法获取访问令牌');
            }
        },

        // 初始化EZUIKit播放器
        initEzuikitPlayer(videoUrl) {
            // 如果播放器已经存在且正常运行，不重复初始化
            if (this.player && this.playerInitialized && !this.videoError) {
                console.log('播放器已初始化，跳过重复初始化');
                return;
            }

            // 销毁之前的播放器
            this.destroyPlayer();

            if (!window.EZUIKit || !window.EZUIKit.EZUIKitPlayer) {
                console.error('EZUIKit SDK未加载');
                this.videoError = true;
                this.videoLoading = false;
                return;
            }

            // 等待DOM更新完成，确保容器尺寸稳定
            this.$nextTick(() => {
                const playerContainer = document.getElementById('camera-player') || this.$refs.cameraPlayer;
                if (!playerContainer) {
                    console.error('播放器容器元素未找到');
                    this.videoError = true;
                    this.videoLoading = false;
                    return;
                }

                try {
                    // 获取容器尺寸，使用getBoundingClientRect获取更准确的尺寸
                    const rect = playerContainer.getBoundingClientRect();
                    const containerWidth = rect.width || playerContainer.offsetWidth || 640;
                    const containerHeight = rect.height || playerContainer.offsetHeight || 360;

                    // 创建播放器
                    this.player = new window.EZUIKit.EZUIKitPlayer({
                        id: 'camera-player',
                        width: containerWidth,
                        height: containerHeight,
                        template: 'pcLive',
                        url: videoUrl,
                        accessToken: this.accessToken,
                        autoplay: true,
                        scaleMode: 1,
                        audio: 0,
                        controls: true,  // 启用播放器自带控制栏（包含全屏、角度调节等功能）
                        header: [],  // 不显示头部
                        footer: [],  // 不显示底部，使用默认控制栏
                        plugin: [],  // 插件配置
                        // 截图回调函数 - 捕获播放器返回的base64图片数据
                        capturePictureCallBack: (data) => {
                            console.log('[采集] 播放器capturePicture回调:', data);
                            // data可能是文件路径或base64数据
                            if (typeof data === 'string' && data.startsWith('data:image')) {
                                // 如果是base64数据，保存它
                                this.handlePlayerCaptureData(data);
                            }
                        }
                    });

                    // 播放器创建成功，立即更新状态（确保按钮可以点击）
                    console.log('播放器对象已创建，player:', this.player);
                    this.videoLoading = false;
                    this.videoError = false;
                    
                    // 强制触发Vue响应式更新
                    this.$forceUpdate();

                // 添加播放器事件监听
                this.player.on('error', (error) => {
                    console.error('萤石播放器错误:', error);
                        this.videoError = true;
                        this.videoLoading = false;
                        this.playerInitialized = false;
                        // 播放失败时更新设备状态为离线(0)
                        const currentCamera = this.cameraList[this.currentCameraIndex];
                        if (currentCamera && currentCamera.id) {
                            this.updateDeviceStatus(currentCamera.deviceSerial, currentCamera.channel, 0);
                        }
                });

                this.player.on('loaded', () => {
                    console.log('播放器加载完成');
                        this.videoLoading = false;
                        this.isPlaying = true;
                        this.playerInitialized = true; // 标记播放器已成功初始化
                    });

                    // 备用检测：如果loaded事件未触发，3秒后也认为播放器可用
                    setTimeout(() => {
                        if (this.player && !this.playerInitialized && !this.videoError) {
                            console.log('播放器备用检测：认为播放器已可用');
                            this.playerInitialized = true;
                            this.videoLoading = false;
                        }
                    }, 3000);

                    this.player.on('play', () => {
                        this.isPlaying = true;
                        // 播放成功时更新设备状态为在线(1)
                        const currentCamera = this.cameraList[this.currentCameraIndex];
                        if (currentCamera && currentCamera.id) {
                            this.updateDeviceStatus(currentCamera.deviceSerial, currentCamera.channel, 1);
                        }
                    });

                    this.player.on('pause', () => {
                        this.isPlaying = false;
                    });

                    // 监听capturePicture事件，获取截图数据（不触发下载）
                    this.player.on('capturePicture', (event) => {
                        console.log('[采集] 播放器capturePicture事件:', event);
                        if (event && event.data) {
                            // event.data包含base64数据，格式: {"fileName":"xxx.jpeg","base64":"data:image/jpeg;base64,..."}
                            if (event.data.base64) {
                                console.log('[采集] 捕获到base64数据，长度:', event.data.base64.length);
                                // 直接使用base64数据，不触发下载
                                this.handlePlayerCaptureData(event.data.base64);
                            } else {
                                console.warn('[采集] 事件数据中没有base64字段:', event.data);
                            }
            } else {
                            console.warn('[采集] 事件数据格式异常:', event);
                        }
                    });

                } catch (error) {
                    console.error('初始化播放器失败:', error);
                    this.videoError = true;
                    this.videoLoading = false;
                }
            });
        },

        // ========== 新增：复用播放器（使用缓存的直播地址）==========
        initOrUpdatePlayer(videoUrl) {
            // 等待DOM更新完成
            this.$nextTick(() => {
                const playerContainer = document.getElementById('camera-player') || this.$refs.cameraPlayer;
                if (!playerContainer) {
                    console.error('播放器容器元素未找到');
                    this.videoError = true;
                    this.videoLoading = false;
                    return;
                }

                try {
                    // 如果播放器已存在且正常运行，直接使用setUrl切换
                    if (this.player && this.playerInitialized && !this.videoError) {
                        console.log('播放器已存在，使用setUrl切换到缓存地址');
                        try {
                            this.player.stop();
                            this.player.setUrl(videoUrl);
                            this.player.play();
                            this.videoLoading = false;
                            console.log('视频源切换成功（使用缓存地址）');
                            return;
                        } catch (e) {
                            console.warn('使用setUrl切换失败，重新初始化播放器:', e);
                            this.destroyPlayer();
                        }
                    }

                    // 创建新播放器
                    const rect = playerContainer.getBoundingClientRect();
                    const containerWidth = rect.width || playerContainer.offsetWidth || 640;
                    const containerHeight = rect.height || playerContainer.offsetHeight || 360;

                    this.player = new window.EZUIKit.EZUIKitPlayer({
                        id: 'camera-player',
                        width: containerWidth,
                        height: containerHeight,
                        template: 'pcLive',
                        url: videoUrl,
                        accessToken: this.accessToken,
                        autoplay: true,
                        scaleMode: 1,
                        audio: 0,
                        controls: true,
                        header: [],
                        footer: [],
                        plugin: [],
                        capturePictureCallBack: (data) => {
                            if (typeof data === 'string' && data.startsWith('data:image')) {
                                this.handlePlayerCaptureData(data);
                            }
                        }
                    });

                    this.videoLoading = false;
                    this.videoError = false;
                    this.$forceUpdate();

                    // 事件监听
                    this.player.on('error', (error) => {
                        console.error('萤石播放器错误:', error);
                        this.videoError = true;
                        this.videoLoading = false;
                        this.playerInitialized = false;
                    });

                    this.player.on('loaded', () => {
                        console.log('播放器加载完成（使用缓存地址）');
                        this.videoLoading = false;
                        this.isPlaying = true;
                        this.playerInitialized = true;
                    });

                    setTimeout(() => {
                        if (this.player && !this.playerInitialized && !this.videoError) {
                            this.playerInitialized = true;
                            this.videoLoading = false;
                        }
                    }, 3000);

                    this.player.on('play', () => {
                        this.isPlaying = true;
                    });

                    this.player.on('pause', () => {
                        this.isPlaying = false;
                    });

                } catch (error) {
                    console.error('初始化播放器失败（使用缓存地址）:', error);
                    this.videoError = true;
                    this.videoLoading = false;
                }
            });
        },

        // 销毁播放器
        destroyPlayer() {
            if (this.player) {
                try {
                    this.player.stop();
                    this.player.destroy();
                } catch (e) {
                    console.error('播放器销毁异常:', e);
                }
                this.player = null;
                this.playerInitialized = false; // 重置初始化标志
            }

            // 清理视频元素，确保彻底释放资源
            const container = document.getElementById('camera-player');
            if (container) {
                // 清空容器的HTML，确保所有资源被释放
                container.innerHTML = '';
                // 移除所有事件监听器
                const videoElement = container.querySelector('video');
                if (videoElement) {
                    videoElement.src = '';
                    videoElement.load();
                }
            }

            // 确保容器元素保持存在，避免DOM结构变化
            if (container && !container.parentNode) {
                // 如果容器被移除，重新添加到video-wrapper中
                const wrapper = this.$refs.cameraPlayer?.parentNode || document.querySelector('.video-wrapper');
                if (wrapper) {
                    wrapper.appendChild(container);
                }
            }
        },

        togglePlay() {
            if (this.player) {
                if (this.isPlaying) {
                    this.player.pause();
                } else {
                    this.player.play();
                }
            }
        },

        toggleMute() {
            if (this.player) {
                // EZUIKit播放器的静音控制
                const currentAudio = this.player.getAudio();
                this.player.setAudio(currentAudio === 1 ? 0 : 1);
                this.isMuted = currentAudio === 1;
            }
        },

        refreshVideo() {
            // 清除之前的防抖定时器
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer);
                this.debounceTimer = null;
            }

            this.videoLoading = true;
            this.videoError = false;
            // 先销毁播放器
            this.destroyPlayer();

            // 防抖：延迟500ms后再加载视频，让之前的流有时间释放
            this.debounceTimer = setTimeout(() => {
                this.$nextTick(() => {
                    this.loadCameraStream();
                });
            }, 500);
        },

        retryLoadVideo() {
            this.videoLoading = true;
            this.videoError = false;
            // 先销毁播放器，等待DOM稳定后再重新加载
            this.destroyPlayer();
            this.$nextTick(() => {
                this.loadCameraStream();
            });
        },

        startCameraPolling() {
            // 每30秒检查一次摄像头状态
            this.cameraTimer = setInterval(() => {
                if (this.player && this.videoError) {
                    console.log('检测到视频异常，重新加载...');
                    this.refreshVideo();
                }
            }, 30000);
        },

        stopCameraPolling() {
            if (this.cameraTimer) {
                clearInterval(this.cameraTimer);
                this.cameraTimer = null;
            }
        },

        // 选择保存路径
        async selectSavePath() {
            try {
                // 检查浏览器是否支持 File System Access API
                if ('showDirectoryPicker' in window) {
                    try {
                        const directoryHandle = await window.showDirectoryPicker({
                            mode: 'readwrite'
                        });
                        this.saveDirectoryHandle = directoryHandle;
                        this.savePath = directoryHandle.name || '已选择文件夹';
                        console.log('已选择保存路径:', directoryHandle.name);
                        
                        // 路径选择成功后，检查播放器状态并提示
                        this.$nextTick(() => {
                            if (!this.player) {
                                console.warn('播放器还未初始化，按钮可能仍为禁用状态');
                                console.log('当前状态 - player:', this.player, 'videoError:', this.videoError, 'videoLoading:', this.videoLoading);
                            } else {
                                console.log('播放器已存在，按钮应该可以点击');
                            }
                        });
                    } catch (error) {
                        if (error.name !== 'AbortError') {
                            console.error('选择路径失败:', error);
                            alert('选择路径失败，将使用浏览器默认下载文件夹');
                            this.savePath = '默认下载文件夹';
                        }
                    }
                } else {
                    // 浏览器不支持 File System Access API，使用默认下载文件夹
                    alert('您的浏览器不支持文件夹选择功能，图片将保存到默认下载文件夹。\n\n建议使用 Chrome 86+ 或 Edge 86+ 浏览器以获得完整功能。');
                    this.savePath = '默认下载文件夹';
                }
            } catch (error) {
                console.error('选择路径异常:', error);
                this.savePath = '默认下载文件夹';
            }
        },

        // 切换采集状态
        toggleCapture() {
            if (this.isCapturing) {
                this.stopCapture();
            } else {
                this.startCapture();
            }
        },

        // 开始采集
        async startCapture() {
            // 先获取最新的抓拍间隔配置
            await this.loadCaptureInterval();

            // 检查播放器
            if (!this.player) {
                alert('播放器未初始化，无法开始采集。\n\n请等待视频加载完成后再试。');
                return;
            }

            // 检查路径
            if (!this.savePath) {
                const shouldSelect = confirm('请先选择保存路径。\n\n点击"选择保存路径"按钮选择文件夹，或点击"确定"使用默认下载文件夹。');
                if (shouldSelect) {
                    await this.selectSavePath();
                    // 如果用户取消了路径选择，使用默认路径
                    if (!this.savePath) {
                        this.savePath = '默认下载文件夹';
                    }
                } else {
                    // 用户取消，不开始采集
                    return;
                }
            }

            // 如果还是没有路径，使用默认路径
            if (!this.savePath) {
                this.savePath = '默认下载文件夹';
            }

            const pathInfo = this.saveDirectoryHandle ? `已选择文件夹: ${this.savePath}` : `保存到: ${this.savePath}`;
            const confirmStart = confirm(`图片将每${this.captureInterval}毫秒自动截取一张。\n${pathInfo}\n\n是否开始采集？`);
            if (!confirmStart) {
                return;
            }

            this.isCapturing = true;
            this.captureCount = 0;

            // 立即截取第一张
            this.captureImage();

            // 每captureInterval毫秒截取一张
            this.captureTimer = setInterval(() => {
                this.captureImage();
            }, this.captureInterval);

            console.log(`开始采集，每${this.captureInterval}毫秒截取一张图片`);
        },

        // 停止采集
        stopCapture() {
            this.isCapturing = false;
            if (this.captureTimer) {
                clearInterval(this.captureTimer);
                this.captureTimer = null;
            }
            console.log(`采集结束，共采集 ${this.captureCount} 张图片`);
        },

        // 更新设备在线状态
        updateDeviceStatus(deviceSerial, channelNo, status) {
            api.put('/api/ezviz/device/status/by-serial', null, {
                params: {
                    deviceSerial: deviceSerial,
                    channelNo: channelNo,
                    status: status
                }
            }).then(() => {
                console.log(`[设备状态] 更新成功: ${deviceSerial}, 通道${channelNo}, status=${status}`);
            }).catch(err => {
                console.error(`[设备状态] 更新失败:`, err);
            });
        },

        // 处理播放器capturePicture返回的base64数据（不触发下载）
        async handlePlayerCaptureData(base64Data) {
            if (!this.saveDirectoryHandle) {
                console.warn('[采集] 未选择保存路径，无法保存播放器截图');
                return;
            }

            if (!base64Data || typeof base64Data !== 'string') {
                console.error('[采集] base64数据无效');
                return;
            }

            try {
                console.log('[采集] 收到播放器base64截图数据，开始解码并保存');
                
                // base64数据格式: "data:image/jpeg;base64,/9j/4AAQ..."
                // 提取base64字符串部分
                let base64String = base64Data;
                let mimeType = 'image/jpeg'; // 默认jpeg
                
                if (base64Data.includes(',')) {
                    const parts = base64Data.split(',');
                    const dataPart = parts[0]; // "data:image/jpeg;base64"
                    base64String = parts[1]; // 实际的base64字符串
                    
                    // 提取MIME类型
                    if (dataPart.includes('image/')) {
                        const mimeMatch = dataPart.match(/image\/([^;]+)/);
                        if (mimeMatch) {
                            mimeType = `image/${mimeMatch[1]}`;
                        }
                    }
                }
                
                console.log('[采集] 图片类型:', mimeType, 'base64长度:', base64String.length);
                
                // 解码base64为Blob
                const byteString = atob(base64String);
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([ab], { type: mimeType });
                
                // 生成文件名（使用时间戳）
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const seconds = String(now.getSeconds()).padStart(2, '0');
                const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
                
                const extension = mimeType === 'image/png' ? 'png' : 'jpeg';
                const fileName = `${year}${month}${day}_${hours}${minutes}${seconds}_${milliseconds}.${extension}`;
                
                // 直接保存到指定文件夹（不触发下载）
                const fileHandle = await this.saveDirectoryHandle.getFileHandle(fileName, { create: true });
                const writable = await fileHandle.createWritable();
                await writable.write(blob);
                await writable.close();
                
                this.captureCount++;
                console.log('[采集] 播放器截图保存成功:', fileName, '已采集', this.captureCount, '张');
            } catch (error) {
                console.error('[采集] 处理播放器截图数据失败:', error);
            }
        },

        // 截取图片 - 通过后端API截图（不触发浏览器下载）
        async captureImage() {
            if (!this.saveDirectoryHandle) {
                console.error('[采集] 未选择保存路径');
                return;
            }

            try {
                console.log('[采集] 调用后端API截图');

                // 获取当前正在观看的摄像头信息
                const currentCamera = this.cameraList[this.currentCameraIndex];
                const deviceSerial = currentCamera ? currentCamera.deviceSerial : null;
                const channelNo = currentCamera ? currentCamera.channel : null;

                console.log('[采集] 当前摄像头信息:', { deviceSerial, channelNo });

                // 调用后端截图API，传递当前摄像头信息
                const response = await api.get('/api/ezviz/capture', {
                    params: {
                        companyId: this.selectedCompanyId,
                        greenhouseId: this.selectedGreenhouseId,
                        deviceSerial: deviceSerial,
                        channelNo: channelNo
                    }
                });
                
                if (response && response.code === '200' && response.data) {
                    // 获取base64数据
                    let base64Data = response.data;
                    
                    // 确保base64数据格式正确
                    if (!base64Data.startsWith('data:image')) {
                        base64Data = 'data:image/jpeg;base64,' + base64Data;
                    }
                    
                    console.log('[采集] 后端返回base64数据，长度:', base64Data.length);
                    
                    // 保存图片
                    const saved = await this.saveImage(base64Data);
                    if (saved) {
                        this.captureCount++;
                        console.log('[采集] 截图保存成功，已采集', this.captureCount, '张');
                    } else {
                        console.error('[采集] 截图保存失败');
                    }
                } else {
                    console.error('[采集] 后端API返回错误:', response.data);
                }
            } catch (error) {
                console.error('[采集] 调用后端截图API失败:', error);
                }
        },

        // 保存图片 - 直接保存到选定目录，不触发浏览器下载
        async saveImage(imageData) {
            try {
                // 生成文件名：使用当前时间
                const now = new Date();
                const year = now.getFullYear();
                const month = String(now.getMonth() + 1).padStart(2, '0');
                const day = String(now.getDate()).padStart(2, '0');
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const seconds = String(now.getSeconds()).padStart(2, '0');
                const milliseconds = String(now.getMilliseconds()).padStart(3, '0');
                
                const fileName = `${year}${month}${day}_${hours}${minutes}${seconds}_${milliseconds}.png`;

                // 将base64转换为blob
                const byteString = atob(imageData.split(',')[1]);
                const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
                const ab = new ArrayBuffer(byteString.length);
                const ia = new Uint8Array(ab);
                for (let i = 0; i < byteString.length; i++) {
                    ia[i] = byteString.charCodeAt(i);
                }
                const blob = new Blob([ab], { type: mimeString });

                // 使用 File System Access API，直接保存到选择的文件夹
                try {
                    const fileHandle = await this.saveDirectoryHandle.getFileHandle(fileName, { create: true });
                    const writable = await fileHandle.createWritable();
                    await writable.write(blob);
                    await writable.close();
                    console.log('[保存] 图片已保存到选定目录:', fileName);
                    return true; // 成功保存
                } catch (error) {
                    console.error('[保存] 保存到选择文件夹失败:', error);
                    alert('保存图片失败，请检查文件夹权限。错误: ' + error.message);
                    return false; // 保存失败
                }
            } catch (error) {
                console.error('[保存] 保存图片失败:', error);
                return false;
        }
        },
    },
    watch: {
        // 移除 immediate: true，避免与 mounted 重复触发
        companyId: {
            handler() {
                console.log('companyId 变化，触发加载摄像头列表');
                this.debounceLoadCamera();
            }
        },
        greenhouseId: {
            handler() {
                console.log('greenhouseId 变化，触发加载摄像头列表');
                this.debounceLoadCamera();
            }
        },
        pondsid: {
            handler(newVal) {
                if (newVal) {
                    this.debounceLoadCamera();
                }
            }
        }
    },
    created() {
        // 只初始化基本数据，不发起网络请求
        // 网络请求统一由 watch 和 mounted 处理
    },
    beforeDestroy() {
        this.stopCameraPolling();
        // 停止轮播
        this.stopCarousel();
        // 停止采集
        this.stopCapture();
        // 清理播放器实例
        this.destroyPlayer();
    },
    mounted() {
        // 摄像头状态轮询已禁用（如果需要可启用）
        // this.startCameraPolling();

        // 监听窗口大小变化，调整播放器尺寸
        this.resizeHandler = () => {
            if (this.player && this.playerInitialized) {
                const playerContainer = document.getElementById('camera-player');
                if (playerContainer) {
                    const rect = playerContainer.getBoundingClientRect();
                    this.player.resize(rect.width, rect.height);
                }
            }
        };
        window.addEventListener('resize', this.resizeHandler);
    },
};
</script>

<style lang="less" scoped>
/* 摄像头区域样式 */
.camera-section {
    width: 100%;
    height: 100%;
    background-color: transparent;
    box-sizing: border-box;

    .camera-container {
        border: 1px solid #0D2451;
        position: relative;
        background: transparent;
        padding: 0;
        height: 100%;
        width: 100%;

        .title {
    position: absolute;
            display: inline-block;
            color: #6EDDF1;
            height: 18px;
            left: 50%;
            transform: translate(-50%, -50%);
    z-index: 10;

            .title-gradient {
                position: relative;
                font-size: 16px;
                background: #09102E;
                display: inline-block;
                padding: 0 10px;
                transform: translateY(-5px);
}
        }

        .angle1, .angle2, .angle3, .angle4 {
            position: absolute;
            width: 12px;
            height: 12px;
            border: 1px solid #6EDDF1;
        }

        .angle1 {
            top: -1px;
            left: -1px;
            border-right: none;
            border-bottom: none;
        }

        .angle2 {
            top: -1px;
            right: -1px;
            border-left: none;
            border-bottom: none;
}

        .angle3 {
            bottom: -1px;
            left: -1px;
            border-right: none;
            border-top: none;
        }

        .angle4 {
            bottom: -1px;
            right: -1px;
            border-left: none;
            border-top: none;
        }

            .video-wrapper {
            width: 100%;
            height: calc(100% - 80px);
            position: relative;
            background: #000;
            margin-top: 25px;
            border-radius: 0;
            overflow: visible;

            #camera-player {
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
                z-index: 1;
            }

            // 强制控制播放器内部视频比例，防止拉伸
            ::v-deep #camera-player video,
            ::v-deep .ezopen-video video,
            ::v-deep video {
                object-fit: contain !important;
            }
            
            // 确保萤石云播放器控制栏可见
            ::v-deep .ezui-player-controls {
                z-index: 10 !important;
            }
            
            ::v-deep .ezui-control-bar {
                z-index: 10 !important;
            }

            .video-loading {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                text-align: center;
                color: #6EDDF1;
                z-index: 5;

                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 4px solid #0D2451;
                    border-top: 4px solid #6EDDF1;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                    margin: 0 auto 10px;
                }

                p {
                    margin: 0;
                    font-size: 14px;
                }
            }

            // 摄像头列表 - 右上角（与标题同一水平线）
            .camera-list-top-right {
                position: absolute;
                top: -15px;  // 向上调整，避免与视频画面重叠
                right: 10px;
                transform: translateY(-50%);
                z-index: 20;  // 确保在最上层，不遮挡播放器
                background: transparent;  // 与模块背景一致
                padding: 8px 10px;
                border-radius: 6px;
                border: none;  // 移除边框，与模块风格一致

                .camera-list-items {
                    display: flex;
                    flex-direction: row;  // 水平排列
                    gap: 8px;

                    .camera-item {
                        background: rgba(110, 221, 241, 0.1);
                        color: #6EDDF1;
                        border: 1px solid #6EDDF1;
                        padding: 6px 12px;
                        border-radius: 4px;
                        cursor: pointer;
                        font-size: 12px;
                        transition: all 0.3s;
                        white-space: nowrap;
                        text-align: center;

                        &:hover {
                            background: rgba(110, 221, 241, 0.3);
                            transform: translateY(-2px);
                        }

                        &.active {
                            background: rgba(110, 221, 241, 0.5);
                            border-color: #6EDDF1;
                            box-shadow: 0 0 8px rgba(110, 221, 241, 0.5);
                            font-weight: 500;
                        }
                    }
                }
            }
        }

        .camera-controls {
            display: flex;
    justify-content: center;
            gap: 10px;
            margin-top: 10px;

            .control-btn {
                background: rgba(110, 221, 241, 0.2);
                color: #6EDDF1;
                border: 1px solid #6EDDF1;
                padding: 5px 15px;
                border-radius: 3px;
                cursor: pointer;
                font-size: 12px;

        &:hover {
                    background: rgba(110, 221, 241, 0.4);
                }
            }
        }

        .camera-capture {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-wrap: wrap;
            gap: 10px;
            padding: 10px 0;
            margin-top: 8px;

            .path-btn, .capture-btn {
                background: rgba(110, 221, 241, 0.2);
                color: #6EDDF1;
                border: 1px solid #6EDDF1;
                padding: 6px 15px;
                border-radius: 3px;
                cursor: pointer;
                font-size: 12px;
                transition: all 0.3s;

                &:hover:not(:disabled) {
                    background: rgba(110, 221, 241, 0.4);
        }

                &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                }
            }

            .capture-btn.capturing {
                background: rgba(255, 0, 0, 0.3);
                border-color: #ff0000;
                color: #ffcccc;
            }

            .save-path, .capture-status {
                color: #6EDDF1;
                font-size: 12px;
        }

            .debug-info {
                color: #888;
                font-size: 11px;
            }
        }
    }
}

@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
